import { Fragment } from 'react';
import { Link, useSearchParams } from 'react-router';
import { BackBar } from '@/components/BackBar';
import { ChevronIcon } from '@/components/Icons';
import { Mark } from '@/components/Mark';
import { Screen } from '@/components/Screen';
import { fromDateKey, isRecordableDay, type DateKey } from '@/domain/date';
import { PRAYERS, QUALITIES, markNoteKey, type PrayerId } from '@/domain/prayer';
import {
  DEFAULT_RANGE,
  RANGES,
  canGoForward,
  nextAnchor,
  previousAnchor,
  rangeFrom,
  summariseRange,
  summariseWindow,
  type Range,
} from '@/domain/report';
import { markFor } from '@/domain/streaks';
import { isDue } from '@bonyaan/prayer-times';
import { useAppState } from '@/state/useAppState';
import { useT } from '@/state/useT';
import { useReportStats } from '@/state/useStreaks';
import { useWindowRecords } from '@/state/useWindowRecords';

/** Where a week and a month fall on a run's bar. */
const MILESTONES = [7, 30] as const;
/**
 * The lengths a bar is ever drawn against: a week, a month, a season, a year.
 *
 * One scale for all twenty bars, so they are comparable with each other — but
 * the shortest of them is a week, not a month. Everything measured against a
 * fixed thirty days meant a screen whose best run was three days drew every
 * bar as a stub about a tenth of the way along, which reads as "you have
 * almost nothing" for a record that is going perfectly well, and left the
 * numeral beside it as the only part of the row saying anything.
 *
 * The scale is the first of these that holds the longest run on screen, so the
 * bar always has a top that means something — a week, then a month — and the
 * milestones stay on it rather than falling off the end.
 *
 * The records are measured on it too, which is what stops a star being drawn
 * past the end of its own bar. It does mean a record far ahead of every
 * current run compresses all twenty fills — the very thing the fixed
 * thirty-day scale was dropped for. The difference is that the stub now has
 * the star beside it saying why it is short, which is the reading the section
 * is for; a scale that fitted the runs and clamped the star to the end would
 * be a tidier bar telling a reader something untrue.
 */
const SCALES = [7, 30, 90, 365] as const;
/**
 * A run of one day is still a run, and at a year's scale it is a fifth of a
 * pixel. Nothing that has happened is ever drawn as nothing.
 */
const MIN_FILL = 2.5;

function scaleFor(longest: number): number {
  return SCALES.find((length) => length >= longest) ?? longest;
}

/**
 * How far a run has ever reached, marked on the bar it is drawn on.
 *
 * Information rather than ornament, so both skins keep it — a bar showing a
 * run of three says nothing about whether three is a start or a collapse, and
 * a star out at forty says which. It replaced a star that appeared on any run
 * past a month: that one landed at the same place on every bar that had one,
 * which read as a stripe of confetti and said nothing a numeral did not.
 *
 * The adult skin drops the pop it arrives with, not the star itself; that is
 * `.app.adult .qstar` in the design system, next to every other animation it
 * turns off.
 */
function RecordStar({ at, delay }: { at: number; delay: number }) {
  return (
    <svg
      className="qstar"
      viewBox="0 0 34 34"
      aria-hidden="true"
      focusable="false"
      style={{ insetInlineStart: `${at}%`, animationDelay: `${delay}ms` }}
    >
      <path
        d="M17 2.5l3.6 7.6 8.4 1-6.1 5.9 1.5 8.3L17 21.4l-7.4 3.9 1.5-8.3-6.1-5.9 8.4-1L17 2.5Z"
        fill="var(--moon)"
        stroke="var(--outline)"
        strokeWidth="2.2"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function Report() {
  const { t, number, weekday, dayOfMonth, dayLabel } = useT();
  const { i18n, today, times, now } = useAppState();

  /**
   * Which week is being read, and how far back the statistics go, live in the
   * URL rather than in state. Opening a cell navigates away to the sheet,
   * which unmounts this screen — so a child who paged back three weeks,
   * filled a prayer in and came back would have landed on this week again. It
   * also makes a reading of the record worth linking to.
   */
  const [search, setSearch] = useSearchParams();
  const week = search.get('week');
  const anchor: DateKey = isRecordableDay(week, today) ? week : today;
  const range = rangeFrom(search.get('range'));

  // Both readings are written on every change, so setting one never drops the
  // other. Defaults are left out, so plain `/report` is this week and a month.
  const paramsFor = (next: { anchor?: DateKey; range?: Range }) => {
    const at = next.anchor ?? anchor;
    const over = next.range ?? range;
    const params: Record<string, string> = {};
    if (at !== today) params.week = at;
    if (over !== DEFAULT_RANGE) params.range = String(over);
    return params;
  };
  const go = (next: { anchor?: DateKey; range?: Range }) =>
    setSearch(paramsFor(next), { replace: true });
  const query = new URLSearchParams(paramsFor({})).toString();
  const here = query ? `/report?${query}` : '/report';

  const { days } = useWindowRecords(anchor);
  // Not windowed, unlike everything above it: a run, a record and a rate are
  // about now, and would say nothing at all inside seven days someone has
  // paged back to. The headings below say so rather than letting the pager
  // seem to move them.
  const history = useReportStats(today, null);
  const prayerStats = history?.prayers ?? [];
  const ranged = summariseRange(history?.days ?? [], today, range);

  // Every bar on the screen is drawn against one scale, so the twenty are
  // comparable with each other rather than each filling its own row. The
  // records are measured on it too: a star past the end of the bar is a record
  // drawn as though it were not there.
  const reach = prayerStats.flatMap((s) => [...Object.values(s.runs), ...Object.values(s.bests)]);
  const scale = scaleFor(Math.max(0, ...reach));
  const fill = (run: number) => (run === 0 ? 0 : Math.max(MIN_FILL, (run / scale) * 100));
  // The prayer on the longest run of the five — but only when one prayer is
  // clearly on it. Three prayers tying at fifty and the pill going to
  // whichever came first in the list is an arbitrary winner, which is worse
  // than no winner: it reads as a judgement the data does not support.
  const best = Math.max(0, ...prayerStats.map((s) => s.runs.prayed));
  const leaders = prayerStats.filter((s) => s.runs.prayed === best);
  const longest: PrayerId | null = best > 0 && leaders.length === 1 ? leaders[0]!.prayer : null;

  const summary = summariseWindow(days, anchor);
  const byDate = new Map(days.map((d) => [d.date, d]));
  const forward = canGoForward(anchor, today);

  /**
   * The window named on both calendars, because the screen already uses both.
   *
   * The header was the Hijri range alone, and the columns directly beneath it
   * are numbered on the Gregorian calendar — so a week reading "Rabīʿ al-Awwal
   * 25 – Rabīʿ al-Ākhir 2" sat on top of columns headed 7 to 13, with nothing
   * anywhere saying the two were the same seven days. Read as ordinals those
   * are the third and fourth months of the year, which is how a reader ends up
   * certain the report has opened on some week at the end of March.
   *
   * Hijri first, because it is the reckoning the app keeps; the Gregorian line
   * sits directly above the columns it names. And nothing at all where the
   * device cannot really render a Hijri date: the same days twice, the second
   * copy labelled as another calendar, is worse than one line.
   */
  const hijriWindow = i18n.hijriRange(fromDateKey(summary.from), fromDateKey(summary.to));
  const plainWindow = i18n.gregorianRange(fromDateKey(summary.from), fromDateKey(summary.to));

  const stats = [
    {
      key: 'recorded',
      value: t('report.recorded', {
        count: number(summary.counts.prayed),
        total: number(summary.total),
      }),
      label: t('report.recordedLabel'),
    },
    { key: 'fullDays', value: number(summary.completeDays), label: t('report.fullDays') },
    { key: 'perfect', value: number(summary.perfectPrayers), label: t('report.perfect') },
    { key: 'bestRun', value: number(summary.bestRun), label: t('report.bestRun') },
  ];

  // The same two figures as the window's, over a range the reader chose. Not
  // the other two: "prayers recorded" is the `prayed` row of the breakdown
  // below, and a tile repeating a row is a number that looks like two.
  const rangeStats = [
    { key: 'fullDays', value: number(ranged.completeDays), label: t('report.fullDays') },
    { key: 'bestRun', value: number(ranged.bestRun), label: t('report.bestRun') },
  ];

  return (
    <Screen nav quietSky header={<BackBar title={t('nav.report')} />}>
      <div className="report-nav" style={{ marginTop: 16 }}>
        <button
          type="button"
          className="stepb sm"
          data-testid="report-prev"
          aria-label={t('report.previous')}
          onClick={() => go({ anchor: previousAnchor(anchor) })}
        >
          <ChevronIcon back />
        </button>
        <div className="report-window" data-testid="report-window">
          {hijriWindow ? (
            <>
              <div>{hijriWindow}</div>
              <div className="report-window-alt">{plainWindow}</div>
            </>
          ) : (
            <div>{plainWindow}</div>
          )}
        </div>
        <button
          type="button"
          className="stepb sm"
          data-testid="report-next"
          aria-label={t('report.next')}
          disabled={!forward}
          onClick={() => forward && go({ anchor: nextAnchor(anchor, today) })}
        >
          <ChevronIcon />
        </button>
      </div>

      <div className="report-grid" style={{ marginTop: 14 }} data-testid="report-grid">
        <div />
        {summary.dates.map((date) => (
          <div key={date} className="report-head">
            <div>{weekday(fromDateKey(date))}</div>
            <div style={{ opacity: 0.7, marginTop: 2 }}>{dayOfMonth(fromDateKey(date))}</div>
          </div>
        ))}

        {PRAYERS.map((prayer) => (
          <Fragment key={prayer}>
            <div className="report-rowlbl">{t(`prayer.${prayer}`)}</div>
            {summary.dates.map((date) => {
              const mark = markFor(byDate.get(date), prayer);
              // A blank cell on a day gone by is "not recorded — you can make
              // it up"; a blank one for a prayer whose adhān has not called
              // today is "later today". The same three empty states as the
              // home rows, in the same words. `date < today` comes first, so a
              // past cell never asks today's clock about itself.
              const due = date < today || isDue(times[prayer], times.fajr, now);
              return (
                <Link
                  key={`${prayer}-${date}`}
                  to={`/log/${prayer}?date=${date}`}
                  state={{ from: here }}
                  className="report-cell"
                  data-testid={`cell-${prayer}-${date}`}
                  // The mark is a drawing, and `aria-hidden` at that, so
                  // without this the thirty-five cells say nothing at all.
                  aria-label={t('report.cellLabel', {
                    prayer: t(`prayer.${prayer}`),
                    day: dayLabel(fromDateKey(date)),
                    note: t(markNoteKey(mark, due)),
                  })}
                >
                  <Mark mark={mark} size={24} />
                </Link>
              );
            })}
          </Fragment>
        ))}
      </div>

      <div className="muted-note" style={{ marginTop: 10 }}>
        {t('report.editHint')} {t('report.legend')}
      </div>

      <div className="sect" style={{ marginTop: 18 }}>
        {t('report.theseDays')}
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          gap: 8,
          marginTop: 8,
        }}
      >
        {stats.map((stat) => (
          <div key={stat.key} className="stat" data-testid={`stat-${stat.key}`}>
            <span className="statnum">{stat.value}</span>
            <span className="statlbl">{stat.label}</span>
          </div>
        ))}
      </div>

      {summary.counts.prayed === 0 ? (
        <div className="muted-note" style={{ marginTop: 14, textAlign: 'center' }}>
          {t('report.empty')}
        </div>
      ) : null}

      <div className="sect" style={{ marginTop: 20 }}>
        {t('report.statistics')}
      </div>
      <div
        className="rangerow"
        role="group"
        aria-label={t('report.rangeLabel')}
        data-testid="report-ranges"
      >
        {RANGES.map((length) => (
          <button
            key={length}
            type="button"
            className={`pill sm${length === range ? ' on' : ''}`}
            data-testid={`range-${length}`}
            // A pressed button, not a tab or a radio: nothing appears or
            // disappears, the same figures are counted over more days.
            aria-pressed={length === range}
            onClick={() => go({ range: length })}
          >
            {t('report.dayCount', { count: length })}
          </button>
        ))}
      </div>
      <div className="muted-note" style={{ marginTop: 7 }} data-testid="report-range-note">
        {t('report.statsNote')} {t('report.statsTotal', { count: ranged.total })}
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          gap: 8,
          marginTop: 8,
        }}
      >
        {rangeStats.map((stat) => (
          <div key={stat.key} className="stat" data-testid={`rstat-${stat.key}`}>
            <span className="statnum">{stat.value}</span>
            <span className="statlbl">{stat.label}</span>
          </div>
        ))}
      </div>
      <div
        className="pstat"
        style={{ marginTop: 8, padding: '9px 11px' }}
        data-testid="report-share"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          {QUALITIES.map((q) => {
            const count = ranged.counts[q.key];
            // The share of the range's prayers, not a run: this is the one
            // place in the app a bar measures how often rather than how long,
            // and the section it sits in is what says so.
            const share = ranged.total === 0 ? 0 : (count / ranged.total) * 100;
            return (
              <div
                key={q.key}
                className={`qline${count === 0 ? ' none' : ''}`}
                data-testid={`qshare-${q.key}`}
                // The bar is a drawing and the numeral has no unit, so without
                // this a reader hears "JAMĀ‘AH 12".
                aria-label={t('report.qualityShare', {
                  quality: t(`quality.${q.key}.short`),
                  count: number(count),
                  total: number(ranged.total),
                })}
              >
                <span className="qdot" style={{ background: `var(--q${q.segment})` }} />
                <span className="qlbl" aria-hidden="true">
                  {t(`quality.${q.key}.short`)}
                </span>
                <span className="qbar" aria-hidden="true">
                  <span
                    className="qfill"
                    style={{ width: `${share}%`, background: `var(--q${q.segment})` }}
                  />
                </span>
                <span className="qnum wide" aria-hidden="true">
                  {number(count)}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="sect" style={{ marginTop: 20 }}>
        {t('report.eachPrayer')}
      </div>
      <div className="muted-note" style={{ marginTop: 5 }}>
        {t('report.eachPrayerNote')}{' '}
        {/* What the far end of a bar means, in words: a bar with no scale on
            it is a shape, and a reader has no way to tell a short run from a
            short scale. And what the star on it means, for the same reason. */}
        <span data-testid="report-scale">
          {scale === SCALES[0]
            ? t('report.scaleWeek')
            : t('report.scaleDays', { count: number(scale) })}
        </span>{' '}
        {t('report.starNote')}
      </div>
      <div
        style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}
        data-testid="prayer-stats"
      >
        {prayerStats.map((stat, row) => {
          const script = t(`prayer.ar.${stat.prayer}`);
          const prayerName = t(`prayer.${stat.prayer}`);
          // The segments a run is alive on. All four lit is a complete ring,
          // which is already what the app means by it — so the card needs no
          // badge of its own, and no card is left as the only one without.
          const held = {
            prayed: stat.runs.prayed > 0,
            onTime: stat.runs.onTime > 0,
            jamaah: stat.runs.jamaah > 0,
            takbirah: stat.runs.takbirah > 0,
          };
          return (
            <div
              key={stat.prayer}
              className="pstat"
              data-testid={`pstat-${stat.prayer}`}
              style={{ padding: '9px 11px' }}
            >
              <div className="pstat-top" style={{ alignItems: 'center', gap: 9 }}>
                <Mark mark={held} size={30} />
                <span className="pstat-name">{prayerName}</span>
                {script ? (
                  <span className="pstat-ar" lang="ar" dir="rtl">
                    {script}
                  </span>
                ) : null}
                {stat.prayer === longest ? (
                  <span className="pstat-run" data-testid={`pstat-longest-${stat.prayer}`}>
                    {t('report.longestRun')}
                  </span>
                ) : null}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginTop: 7 }}>
                {QUALITIES.map((q, i) => {
                  const run = stat.runs[q.key];
                  const record = stat.bests[q.key];
                  const delay = row * 110 + i * 70;
                  return (
                    <div
                      key={q.key}
                      className={`qline${run === 0 ? ' none' : ''}`}
                      data-testid={`qrun-${stat.prayer}-${q.key}`}
                      // The bar is a drawing and the numeral has no unit, so
                      // without this a reader hears "JAMĀ‘AH 0" — and the star
                      // out along the bar is not spoken at all.
                      aria-label={
                        record > 0
                          ? t('report.prayerQualityBest', {
                              prayer: prayerName,
                              quality: t(`quality.${q.key}.short`),
                              run: t('report.inARow', { count: run }),
                              best: t('report.dayCount', { count: record }),
                            })
                          : t('report.prayerQuality', {
                              prayer: prayerName,
                              quality: t(`quality.${q.key}.short`),
                              run: t('report.inARow', { count: run }),
                            })
                      }
                    >
                      <span className="qdot" style={{ background: `var(--q${q.segment})` }} />
                      <span className="qlbl" aria-hidden="true">
                        {t(`quality.${q.key}.short`)}
                      </span>
                      <span className="qbar" aria-hidden="true">
                        {/* A milestone past the scale is not drawn: it would
                            sit off the end of the bar. The one that lands
                            exactly on the end is the scale, and saying so is
                            the whole point of marking it. */}
                        {MILESTONES.filter((m) => m <= scale).map((m) => (
                          <span
                            key={m}
                            className="qnotch"
                            style={{ insetInlineStart: `${(m / scale) * 100}%` }}
                          />
                        ))}
                        <span
                          className="qfill"
                          style={{
                            width: `${fill(run)}%`,
                            background: `var(--q${q.segment})`,
                            animationDelay: `${delay}ms`,
                          }}
                        />
                        {record > 0 ? <RecordStar at={fill(record)} delay={delay + 300} /> : null}
                      </span>
                      <span className="qnum" aria-hidden="true">
                        {number(run)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </Screen>
  );
}
